import { useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUp, Loader2, Sparkles, ReceiptText } from "lucide-react";

export function ClientInvoiceUploader({ onPosted }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [fields, setFields] = useState(null);
  const [preview, setPreview] = useState(null);

  const reset = () => {
    setFile(null); setPreview(null); setFields(null);
    setPreviewing(false); setParsing(false); setPosting(false);
  };

  const previewFile = async () => {
    if (!file) return toast.error("Choose a PDF first");
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not read invoice");
    } finally { setPreviewing(false); }
  };

  const parse = async () => {
    if (!file) return toast.error("Choose a PDF first");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/parse-async", fd);
      let job = data;
      const started = Date.now();
      while (job.status === "processing" && Date.now() - started < 300000) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await api.get(`/invoices/parse-jobs/${data.job_id}`);
        job = { ...res.data, job_id: data.job_id };
      }
      if (job.status === "error") throw new Error(job.error || "Could not parse invoice");
      if (job.status !== "done") throw new Error("Parsing timed out");
      const f = job.invoice || {};
      // Rename 'vendor' to 'client' semantically — same API but user-facing label differs
      setFields({ ...f, client: f.vendor, category: f.category || "Services" });
      toast.success("Invoice fields extracted — review before saving");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not parse invoice");
    } finally { setParsing(false); }
  };

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const post = async () => {
    if (!fields?.client || !fields?.amount) return toast.error("Client name and amount are required");
    setPosting(true);
    try {
      const { data } = await api.post("/invoices/post-as-receivable", { ...fields, vendor: fields.client });
      toast.success(`Receivable created for ${data.client}`);
      setOpen(false); reset();
      onPosted && onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save receivable");
    } finally { setPosting(false); }
  };

  let stage = "pick";
  if (fields) stage = "review";
  else if (preview) stage = "preview";

  return (
    <Card className="p-5" data-testid="client-invoice-uploader">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center shrink-0">
            <ReceiptText className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              Upload Client Invoice
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-brand2 border border-brand2/40 rounded-full px-2 py-0.5">
                <Sparkles className="h-3 w-3" /> AI extraction
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Upload the invoice PDF you issued to a client — AI extracts client, invoice no., date, GST, category and amount. Review, confirm and it lands in ARR Receivable.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="open-client-invoice-btn" className="gap-2 shrink-0">
          <FileUp className="h-4 w-4" /> Upload Client Invoice
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Client Invoice</DialogTitle>
            <DialogDescription>
              {stage === "pick" && "Upload the PDF invoice you issued to your client."}
              {stage === "preview" && "Confirm the file preview looks right before AI extraction."}
              {stage === "review" && "Review and edit the extracted fields, then save as a Receivable."}
            </DialogDescription>
          </DialogHeader>

          {stage === "pick" && (
            <div className="py-2">
              <Label className="text-xs mb-1.5 block">Invoice PDF</Label>
              <Input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="client-inv-file" />
            </div>
          )}

          {stage === "preview" && (
            <div className="py-2 space-y-3">
              <p className="text-sm"><span className="font-medium">{file?.name}</span> · {preview.pages} page(s) · {preview.text_chars} chars.</p>
              <p className="text-xs text-muted-foreground">Confirm to run AI extraction.</p>
            </div>
          )}

          {stage === "review" && (
            <div className="py-1 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Client</Label>
                <Input value={fields.client || ""} onChange={(e) => setField("client", e.target.value)} data-testid="ar-inv-client" className="mt-1" />
              </div>
              <div><Label className="text-xs">Invoice No.</Label><Input value={fields.invoice_no || ""} onChange={(e) => setField("invoice_no", e.target.value)} data-testid="ar-inv-number" className="mt-1" /></div>
              <div><Label className="text-xs">Category</Label><Input value={fields.category || ""} onChange={(e) => setField("category", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Invoice Date</Label><Input type="date" value={fields.invoice_date || ""} onChange={(e) => setField("invoice_date", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Due Date</Label><Input type="date" value={fields.due_date || ""} onChange={(e) => setField("due_date", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Sub-total</Label><Input type="number" step="0.01" value={fields.sub_total || 0} onChange={(e) => setField("sub_total", Number(e.target.value))} className="mt-1" /></div>
              <div><Label className="text-xs">GST / Tax</Label><Input type="number" step="0.01" value={fields.gst_amount || 0} onChange={(e) => setField("gst_amount", Number(e.target.value))} className="mt-1" /></div>
              <div><Label className="text-xs">Total Receivable</Label><Input type="number" step="0.01" value={fields.amount || 0} onChange={(e) => setField("amount", Number(e.target.value))} data-testid="ar-inv-amount" className="mt-1" /></div>
              <div>
                <Label className="text-xs">Currency</Label>
                <select value={fields.currency || "INR"} onChange={(e) => setField("currency", e.target.value)} className="mt-1 w-full h-10 rounded-md bg-background border border-input px-3 text-sm">
                  {["INR", "USD", "EUR", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Client GSTIN</Label><Input value={fields.gstin || ""} onChange={(e) => setField("gstin", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Client PAN</Label><Input value={fields.pan || ""} onChange={(e) => setField("pan", e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label className="text-xs">Description</Label><Input value={fields.description || ""} onChange={(e) => setField("description", e.target.value)} className="mt-1" /></div>
              <div className="col-span-2 flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2 mt-1">
                <span>Saves to <b className="text-foreground">ARR Receivable</b> with status <b className="text-foreground">Payable</b> until paid.</span>
                <span className="font-mono text-foreground">{formatMoney(fields.amount || 0, fields.currency || "INR")}</span>
              </div>
            </div>
          )}

          <DialogFooter>
            {stage === "pick" && (<><Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button><Button onClick={previewFile} disabled={previewing || !file} data-testid="preview-client-inv-btn" className="gap-2">{previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}{previewing ? "Reading…" : "Preview"}</Button></>)}
            {stage === "preview" && (<><Button variant="outline" onClick={() => setPreview(null)} disabled={parsing}>Back</Button><Button onClick={parse} disabled={parsing} data-testid="confirm-client-inv-parse-btn" className="gap-2">{parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{parsing ? "AI extracting…" : "Confirm & AI-Extract"}</Button></>)}
            {stage === "review" && (<><Button variant="outline" onClick={() => setFields(null)} disabled={posting}>Back</Button><Button onClick={post} disabled={posting || !fields?.client || !fields?.amount} data-testid="save-client-inv-btn" className="gap-2">{posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}Save as Receivable</Button></>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
